/** Draft row drag-select: plain drag = range; ⌘/Ctrl+drag = add; ⌘/Ctrl+Shift+drag = subtract. */

import { useCallback, useEffect, useRef, type Dispatch, type PointerEvent, type SetStateAction } from 'react';

import type { TabKind } from '../shared/types';

export interface UseDraftRowDragSelectionParams {
  activeTab: TabKind;
  displayedDropIds: string[];
  /** Latest selection (ref-synced) for modifier drags. */
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  dragHandleSelector?: string;
}

function modifierDragMode(e: PointerEvent<HTMLElement>): 'add' | 'subtract' | 'replace' {
  const metaOrCtrl = e.metaKey || e.ctrlKey;
  if (!metaOrCtrl) return 'replace';
  return e.shiftKey ? 'subtract' : 'add';
}

export function useDraftRowDragSelection(params: UseDraftRowDragSelectionParams) {
  const { activeTab, displayedDropIds, selectedIds, setSelectedIds, dragHandleSelector } = params;
  const anchorIndexRef = useRef<number | null>(null);
  const pointerDownRef = useRef(false);
  const dragActiveRef = useRef(false);
  const suppressClickRef = useRef(false);
  const idsRef = useRef(displayedDropIds);
  idsRef.current = displayedDropIds;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const dragModeRef = useRef<'add' | 'subtract' | 'replace'>('replace');
  const baseSelectionRef = useRef<string[]>([]);

  const lastRangeKeyRef = useRef('');
  const pendingRangeRef = useRef<{ lo: number; hi: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const applyRangeImmediate = useCallback((lo: number, hi: number) => {
    const rangeIds = idsRef.current.slice(lo, hi + 1);
    const mode = dragModeRef.current;
    if (mode === 'add') {
      setSelectedIds([...new Set([...baseSelectionRef.current, ...rangeIds])]);
      return;
    }
    if (mode === 'subtract') {
      const remove = new Set(rangeIds);
      setSelectedIds(baseSelectionRef.current.filter((id) => !remove.has(id)));
      return;
    }
    const key = `${lo}:${hi}`;
    if (lastRangeKeyRef.current === key) return;
    lastRangeKeyRef.current = key;
    setSelectedIds(rangeIds);
  }, [setSelectedIds]);

  const flushScheduledRange = useCallback(() => {
    rafRef.current = null;
    const p = pendingRangeRef.current;
    if (!p) return;
    applyRangeImmediate(p.lo, p.hi);
  }, [applyRangeImmediate]);

  const scheduleRange = useCallback(
    (from: number, to: number) => {
      const lo = Math.min(from, to);
      const hi = Math.max(from, to);
      pendingRangeRef.current = { lo, hi };
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(flushScheduledRange);
    },
    [flushScheduledRange],
  );

  const endPointer = useCallback(() => {
    if (pointerDownRef.current && dragActiveRef.current) suppressClickRef.current = true;
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const p = pendingRangeRef.current;
    pendingRangeRef.current = null;
    if (p) applyRangeImmediate(p.lo, p.hi);
    pointerDownRef.current = false;
    anchorIndexRef.current = null;
    dragActiveRef.current = false;
    dragModeRef.current = 'replace';
    lastRangeKeyRef.current = '';
  }, [applyRangeImmediate]);

  useEffect(() => {
    window.addEventListener('pointerup', endPointer);
    window.addEventListener('pointercancel', endPointer);
    return () => {
      window.removeEventListener('pointerup', endPointer);
      window.removeEventListener('pointercancel', endPointer);
    };
  }, [endPointer]);

  const onRowPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>, dropId: string) => {
      if (activeTab !== 'drafts') return;
      const el = e.target as HTMLElement;
      if (el.closest('a, button')) return;
      if (dragHandleSelector && el.closest(dragHandleSelector)) return;
      const idx = displayedDropIds.indexOf(dropId);
      if (idx < 0) return;
      e.preventDefault();
      window.getSelection()?.removeAllRanges();
      lastRangeKeyRef.current = '';
      pendingRangeRef.current = null;
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const mode = modifierDragMode(e);
      dragModeRef.current = mode;
      baseSelectionRef.current = mode === 'replace' ? [] : [...selectedIdsRef.current];
      pointerDownRef.current = true;
      dragActiveRef.current = false;
      anchorIndexRef.current = idx;
    },
    [activeTab, displayedDropIds, dragHandleSelector],
  );

  const onRowPointerEnter = useCallback(
    (dropId: string) => {
      if (!pointerDownRef.current || activeTab !== 'drafts') return;
      const anchor = anchorIndexRef.current;
      if (anchor == null) return;
      const idx = displayedDropIds.indexOf(dropId);
      if (idx < 0) return;
      if (idx === anchor) return;
      dragActiveRef.current = true;
      window.getSelection()?.removeAllRanges();
      scheduleRange(anchor, idx);
    },
    [activeTab, displayedDropIds, scheduleRange],
  );

  const consumeSuppressClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return { onRowPointerDown, onRowPointerEnter, consumeSuppressClick };
}
